import { useEffect, useRef, useState } from 'react'
import { formatAuthorTime, type ScenarioClockConfig } from './format'
import { isoFromStoryMs, storyMs } from './storyTime'
import type { WindKeyframe } from './types'

export function WindDegControl({
  fromDeg,
  cardinal,
  editing,
  disabled = false,
  onChange,
}: {
  fromDeg: number
  /** Compact compass letters after the degree (e.g. W, SW). */
  cardinal?: string
  editing: boolean
  disabled?: boolean
  onChange: (fromDeg: number) => void
}) {
  const [text, setText] = useState(String(Math.round(fromDeg)))
  const focusedRef = useRef(false)

  useEffect(() => {
    if (focusedRef.current) return
    setText(String(Math.round(fromDeg)))
  }, [fromDeg])

  function commit(raw: string): void {
    if (disabled) return
    const n = Number(raw.trim())
    if (!Number.isFinite(n)) {
      setText(String(Math.round(fromDeg)))
      return
    }
    const next = ((Math.round(n) % 360) + 360) % 360
    setText(String(next))
    if (next !== Math.round(fromDeg)) onChange(next)
  }

  function nudge(delta: number): void {
    if (disabled) return
    const next = (((Math.round(fromDeg) + delta) % 360) + 360) % 360
    onChange(next)
  }

  const cardinalEl = cardinal ? (
    <span className="sc-wind-deg-cardinal" aria-hidden="true">
      {cardinal}
    </span>
  ) : null

  if (!editing) {
    return (
      <div className="sc-wind-deg">
        <span className="sc-wind-deg-num">{Math.round(fromDeg)}</span>
        <span className="sc-wind-deg-unit">{'\u00B0'}</span>
        {cardinalEl}
      </div>
    )
  }

  return (
    <div className={`sc-wind-deg-edit${disabled ? ' is-disabled' : ''}`}>
      <button
        type="button"
        className="sc-wind-deg-step"
        aria-label="Decrease one degree"
        tabIndex={-1}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => nudge(-1)}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <path d="M1.5 5h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
      <div className="sc-wind-deg-value">
        <input
          className="sc-wind-deg-input"
          inputMode="numeric"
          aria-label="Wind from degrees"
          disabled={disabled}
          value={text}
          onFocus={() => {
            focusedRef.current = true
          }}
          onChange={(e) => setText(e.target.value.replace(/[^\d-]/g, ''))}
          onBlur={() => {
            focusedRef.current = false
            commit(text)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              ;(e.target as HTMLInputElement).blur()
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              nudge(1)
            } else if (e.key === 'ArrowDown') {
              e.preventDefault()
              nudge(-1)
            }
          }}
        />
        <span className="sc-wind-deg-unit" aria-hidden="true">
          {'\u00B0'}
        </span>
        {cardinalEl}
      </div>
      <button
        type="button"
        className="sc-wind-deg-step"
        aria-label="Increase one degree"
        tabIndex={-1}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => nudge(1)}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <path d="M1.5 5h7M5 1.5v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  )
}

/** Playhead this close to a wind mark counts as on it, so Update edits that mark. */
export const WIND_MARK_MATCH_MS = 400

export function nearestWindFrameIndex(
  frames: readonly { at: string }[],
  ms: number,
  atMs: (iso: string) => number,
  tol = WIND_MARK_MATCH_MS,
): number {
  let best = -1
  let bestD = tol
  frames.forEach((frame, index) => {
    const d = Math.abs(atMs(frame.at) - ms)
    if (d <= tol && (best < 0 || d < bestD)) {
      best = index
      bestD = d
    }
  })
  return best
}

export function WindKeyframeScrubber({
  frames,
  playheadIso,
  clock,
  disabled = false,
  label = 'Timespan',
  hint,
  onSeek,
}: {
  frames: WindKeyframe[]
  playheadIso: string
  clock: ScenarioClockConfig
  disabled?: boolean
  label?: string
  hint?: string
  onSeek: (iso: string) => void
}) {
  const first = frames[0] ? storyMs(frames[0].at) : 0
  const last = frames.length ? storyMs(frames[frames.length - 1]!.at) : 0
  const span = Math.max(1, last - first)
  const playMs = storyMs(playheadIso)
  const value = frames.length >= 2 ? Math.min(last, Math.max(first, playMs)) : playMs
  const liveMsRef = useRef(value)
  const draggingRef = useRef(false)

  if (!draggingRef.current) liveMsRef.current = value

  if (frames.length < 2) return null

  /** End a drag without soft-snap — tick marks are the precise landing tool. */
  function endDrag(): void {
    draggingRef.current = false
  }

  return (
    <div className="sc-wind-scrub">
      <div className="sc-flbl">{label}</div>
      {hint ? <p className="sc-hint">{hint}</p> : null}
      <div className="sc-wind-scrub-track">
        <input
          type="range"
          className="sc-wind-scrub-range"
          min={first}
          max={last}
          step={1}
          value={value}
          disabled={disabled}
          aria-label="Scrub wind timespan"
          onPointerDown={(e) => {
            if (disabled) return
            draggingRef.current = true
            e.currentTarget.setPointerCapture(e.pointerId)
          }}
          onChange={(e) => {
            if (disabled) return
            const ms = Number(e.target.value)
            liveMsRef.current = ms
            onSeek(isoFromStoryMs(ms))
          }}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onLostPointerCapture={endDrag}
        />
        <div className="sc-wind-scrub-ticks">
          {frames.map((frame, index) => (
            <button
              key={`${frame.at}-${index}`}
              type="button"
              className="sc-wind-scrub-tick"
              tabIndex={-1}
              title={formatAuthorTime(frame.at, clock)}
              aria-label={`Jump to ${formatAuthorTime(frame.at, clock)}`}
              style={{ left: `${((storyMs(frame.at) - first) / span) * 100}%` }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (disabled) return
                draggingRef.current = false
                liveMsRef.current = storyMs(frame.at)
                onSeek(frame.at)
              }}
            />
          ))}
        </div>
      </div>
      <div className="sc-wind-scrub-ends" aria-hidden="true">
        <span>{formatAuthorTime(frames[0]!.at, clock)}</span>
        <span>{formatAuthorTime(frames[frames.length - 1]!.at, clock)}</span>
      </div>
    </div>
  )
}

/**
 * Read-only copy of the story window. The thumb follows the story playhead.
 * A tick seeks that same clock. The track itself does not scrub.
 */
export function WindStoryMirror({
  durationMs,
  elapsedMs,
  frames,
  startLabel,
  endLabel,
  onSeekFrame,
}: {
  durationMs: number
  elapsedMs: number
  frames: { atMs: number; title: string }[]
  startLabel: string
  endLabel: string
  onSeekFrame: (ms: number) => void
}) {
  const span = Math.max(1, durationMs)
  const head = Math.min(span, Math.max(0, elapsedMs))
  const headPct = (head / span) * 100
  return (
    <div className="sc-wind-mirror">
      <div className="sc-flbl">Timespan</div>
      <div className="sc-wind-mirror-track">
        <div className="sc-wind-mirror-line" aria-hidden="true" />
        <div
          className="sc-wind-mirror-thumb"
          style={{ left: `${headPct}%` }}
          aria-hidden="true"
        />
        {frames.map((frame, index) => {
          const at = Math.min(span, Math.max(0, frame.atMs))
          const pct = (at / span) * 100
          return (
            <button
              key={`${frame.atMs}-${index}`}
              type="button"
              className="sc-wind-scrub-tick"
              style={{ left: `${pct}%` }}
              title={frame.title}
              aria-label={frame.title}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSeekFrame(Math.min(span, Math.max(0, frame.atMs)))}
            />
          )
        })}
      </div>
      <div className="sc-wind-scrub-ends" aria-hidden="true">
        <span>{startLabel}</span>
        <span>{endLabel}</span>
      </div>
    </div>
  )
}
